import { action, atom, batch, withInit } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import i18next from 'i18next'
import { match } from 'ts-pattern'

import { AsyncStateN, Result } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { TxHash } from '@root/entities/transaction'
import { environmentAtom } from '@root/shared'
import { reatomNumericInput } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui/error-snackbar'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { extendPremiumAction } from './extend-premium-request'
import { processBoostersPurchaseAction } from './premium-requests'

import type { CurrencyModel } from '@root/entities/currency'
import type { PremiumId } from '@root/entities/premium'
import type { PaymentSourceWallet } from '@root/features/wallet/payment-wallets/atoms'
import type { Error } from '@root/shared'
import type { ApiError, PremiumApi } from '@root/shared/api'

export type PurchaseBoostersType = PremiumApi.PurchaseTypeDto

export type PurchaseBoostersValidationError =
  | {
      type: 'InsufficientBalance'
    }
  | {
      type: 'NotEnoughBoostersForPremium'
    }
  | {
      type: 'InvalidBoosters'
    }
  | {
      type: 'PortalLinkInvalid'
    }
  | {
      type: 'NoWallet'
    }

export const reatomPurchaseBoosters = (
  {
    currency,
    type,
    premiumId,
  }: {
    currency: CurrencyModel
    premiumId: PremiumId | undefined
    type: PremiumApi.PurchaseTypeDto | 'extend'
  },
  name: string,
) => {
  const paymentSourceAtom = atom<PaymentSourceWallet | undefined>(
    undefined,
    `${name}.paymentSourceAtom`,
  )

  const portalLinkAtom = atom<string>('', `${name}.portalLinkAtom`)

  // eslint-disable-next-line @reatom/unit-naming-rule
  const miniumBoostersAtom = atom((ctx) => {
    const { extendPremiumBoosters, premiumBoosters, minimumBoostersPurchase } =
      ctx.get(environmentAtom).premium

    return match(type)
      .with('boosters', () => minimumBoostersPurchase)
      .with('premium', () => premiumBoosters)
      .with('extend', () => extendPremiumBoosters)
      .exhaustive()
  })

  const boostersCountAtom = reatomNumericInput(
    {},
    `${name}.boostersCountAtom`,
  ).pipe(
    withInit((ctx) => {
      boostersCountAtom.setRaw(ctx, ctx.get(miniumBoostersAtom).toString())
      // Hack, atom is readonly, so we assign raw directly here
      return BigNumber(0)
    }),
  )

  const setBoostersCountAction = action((ctx, boosters: number) => {
    boostersCountAtom.setRaw(ctx, boosters.toString())
  }, `${name}.setBoostersCountAction`)

  const requestStateAtom = atom(
    AsyncStateN.notInitiated<
      Error.UnknownError | ApiError.ApiError,
      { transactionHash: TxHash }
    >(),
    `${name}.requestStateAtom`,
  )

  const boostersPriceNativeAtom = atom((ctx) => {
    const network = ctx.spy(selectedNetworkAtom)
    const premiumConfig = ctx.spy(environmentAtom).premium
    const boosterPrice =
      network === Network.Ethereum
        ? premiumConfig.boosterPriceEth
        : premiumConfig.boosterPriceSol

    const boosters = ctx.spy(boostersCountAtom)
    const boostersPrice = BigNumber(boosters).multipliedBy(boosterPrice)

    return boostersPrice
  }, `${name}.boostersPriceNativeAtom`)

  const paymentSourceBalance = atom((ctx) => {
    const paymentSource = ctx.spy(paymentSourceAtom)
    if (!paymentSource) {
      return BigNumber(0)
    }
    const nativeBalance = ctx.spy(paymentSource.wallet.nativeAssetBalance)

    return nativeBalance
  }, `${name}.paymentSourceBalance`)

  const validationAtom = atom(
    (ctx): PurchaseBoostersValidationError | undefined => {
      const paymentSource = ctx.spy(paymentSourceAtom)
      if (!paymentSource) {
        return { type: 'NoWallet' }
      }

      const boostersCount = ctx.spy(boostersCountAtom)
      if (boostersCount.isZero()) {
        return { type: 'InvalidBoosters' }
      }

      const portalLink = ctx.spy(portalLinkAtom)
      if (type === 'premium' && !portalLink) {
        return { type: 'PortalLinkInvalid' }
      }

      const boostersPriceNative = ctx.spy(boostersPriceNativeAtom)
      const walletBalance = ctx.spy(paymentSourceBalance)
      if (walletBalance.lt(boostersPriceNative)) {
        return {
          type: 'InsufficientBalance',
        }
      }

      const minimumBoosters = ctx.get(miniumBoostersAtom)
      if (boostersCount.lt(minimumBoosters)) {
        return { type: 'NotEnoughBoostersForPremium' }
      }
    },
    `${name}.validationAtom`,
  )

  const purchaseBoostersAction = action(async (ctx) => {
    const paymentSource = ctx.get(paymentSourceAtom)
    if (!paymentSource) {
      requestStateAtom(ctx, AsyncStateN.notInitiated())
      return
    }
    requestStateAtom(ctx, AsyncStateN.loading())

    const boosters = ctx.get(boostersCountAtom)
    const portalLink = ctx.get(portalLinkAtom)
    const network = ctx.get(selectedNetworkAtom)

    if (type === 'extend') {
      if (!premiumId) {
        return
      }

      const extendResult = await ctx.schedule(() =>
        extendPremiumAction(ctx, {
          premiumId,
          walletId: paymentSource.wallet.id,
          network: Network.toDto(network),
        }),
      )
      batch(ctx, () => {
        const mappedResult = extendResult.pipe(
          Result.map((result) => ({
            transactionHash: TxHash(result.premium.purchaseTxHash || ''),
          })),
        )
        requestStateAtom(ctx, AsyncStateN.fromResult(mappedResult))

        if (Result.isFailure(extendResult)) {
          showSnackbarAction(ctx, {
            text: i18next.t('premium.pay.extend-error'),
            intent: 'error',
          })
          return
        }
      })

      return
    }

    const purchaseResult = await ctx.schedule(() =>
      processBoostersPurchaseAction(ctx, {
        boosters: boosters.toNumber(),
        currencyAddress: currency.address,
        type: type,
        network,
        walletId: paymentSource.wallet.id,
        portalLink: portalLink,
      }),
    )

    batch(ctx, () => {
      const mappedResult = purchaseResult.pipe(
        Result.map((result) => ({
          transactionHash: TxHash(result.premium.purchaseTxHash || ''),
        })),
      )
      requestStateAtom(ctx, AsyncStateN.fromResult(mappedResult))

      if (Result.isFailure(purchaseResult)) {
        showErrorSnackbarAction(ctx, purchaseResult.failure)
        return
      }
    })
  }, `${name}.purchaseBoostersAction`)

  const resetAction = action((ctx) => {
    requestStateAtom(ctx, AsyncStateN.notInitiated())
  }, `${name}.resetAction`)

  return {
    portalLinkAtom,
    paymentSourceAtom,
    boostersCountAtom,
    boostersPriceNativeAtom,
    setBoostersCountAction,
    purchaseBoostersAction,
    validationAtom,
    miniumBoostersAtom,
    resetAction,
    requestStateAtom,
  }
}
export type PurchaseBoostersReatom = ReturnType<typeof reatomPurchaseBoosters>
